import { useReducedMotion } from 'framer-motion'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { TrendingUp } from 'lucide-react'
import {
  buildOperacaoTrendChartData,
  hasOperacaoTrendData,
} from '@/components/Dashboard/operacaoChart.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'

type Props = {
  createdByMonth: { month: string; count: number; value: number }[]
  closedByMonth: { month: string; count: number; revenue: number }[]
  pipelineCount: number
  valuesVisible?: boolean
}

export function OperacaoTrendChart({
  createdByMonth,
  closedByMonth,
  pipelineCount,
  valuesVisible = false,
}: Props) {
  const reduced = useReducedMotion()
  const chartData = buildOperacaoTrendChartData({ createdByMonth, closedByMonth })
  const hasData = hasOperacaoTrendData(chartData)

  return (
    <div className='rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm'>
      <div className='mb-5 flex flex-wrap items-start justify-between gap-3'>
        <div>
          <div className='flex items-center gap-2'>
            <TrendingUp size={16} className='text-accent' aria-hidden />
            <h3 className='text-base font-semibold text-text-primary'>Movimento no período</h3>
          </div>
          <p className='mt-1 text-sm text-text-muted'>
            Negócios criados (barras) e fechamentos ganhos (linha)
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3 text-xs text-text-muted'>
          <span className='inline-flex items-center gap-1.5'>
            <span className='h-2.5 w-2.5 rounded-sm bg-[var(--brand-primary)]' aria-hidden />
            Novos deals
          </span>
          <span className='inline-flex items-center gap-1.5'>
            <span className='h-0.5 w-4 rounded bg-emerald-500' aria-hidden />
            Ganhos
          </span>
        </div>
      </div>

      {hasData ? (
        <ResponsiveContainer width='100%' height={280}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray='3 3' stroke='var(--border)' vertical={false} />
            <XAxis
              dataKey='month'
              tick={{ fill: 'var(--font-color-muted)', fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              yAxisId='left'
              allowDecimals={false}
              tick={{ fill: 'var(--font-color-muted)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={32}
            />
            <YAxis
              yAxisId='right'
              orientation='right'
              allowDecimals={false}
              tick={{ fill: 'var(--font-color-muted)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={32}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              }}
              formatter={(rawValue, rawName) => {
                const value = Number(rawValue ?? 0)
                const name = String(rawName ?? '')
                if (name === 'novosValor') {
                  return [formatDashboardCurrency(value, valuesVisible), 'Valor novos deals']
                }
                if (name === 'receitaFechada') {
                  return [formatDashboardCurrency(value, valuesVisible), 'Receita fechada']
                }
                if (name === 'novos') {
                  return [formatDashboardNumber(value, valuesVisible), 'Novos deals']
                }
                return [formatDashboardNumber(value, valuesVisible), 'Deals ganhos']
              }}
            />
            <Bar
              yAxisId='left'
              dataKey='novos'
              fill='var(--brand-primary)'
              radius={[6, 6, 0, 0]}
              maxBarSize={48}
              isAnimationActive={!reduced}
            />
            <Line
              yAxisId='right'
              type='monotone'
              dataKey='fechados'
              stroke='#10b981'
              strokeWidth={2.5}
              dot={{ r: 3, fill: '#10b981', strokeWidth: 0 }}
              isAnimationActive={!reduced}
            />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <div className='flex h-[280px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-dark)]/10 px-6 text-center'>
          <p className='text-sm font-medium text-text-primary'>Sem movimento no período filtrado</p>
          <p className='max-w-md text-sm text-text-muted'>
            {pipelineCount > 0
              ? `${pipelineCount} negociações seguem ativas no pipeline, mas não houve entrada nova nem fechamento com data neste intervalo. Tente “Último trimestre” ou “Este ano”.`
              : 'Nenhum negócio criado ou fechado neste intervalo. Ajuste o período no topo da página.'}
          </p>
        </div>
      )}
    </div>
  )
}
